import { fetcher } from "@repo/api";
import useSWR, { mutate } from "swr";

import { type AnswersData, type QuestionnaireSettings } from "./types";

const createQuestionnaireFetcher =
  (country: string, language: string) => async () => {
    try {
      const url = `${
        import.meta.env.VITE__LOCALE_PREFIX
          ? `${import.meta.env.VITE__LOCALE_PREFIX}/`
          : "/"
      }${country}/${language}/saleslead/settings/questionnaire.json`;

      const res = await fetch(url);
      return await res?.json();
    } catch {
      /* If file is not found etc. assume no setting exists */
      return "";
    }
  };

export function useQuestionnaireAnswers(opportunityId?: number) {
  return useSWR<AnswersData>(
    opportunityId ? `/${opportunityId}/questionnaire?type=NEED_ANALYSIS` : null,
    async (url: string) => {
      const res = await fetcher.opportunity.get<AnswersData>(url);
      return res;
    }
  );
}

export const saveQuestionnaire = async (
  opportunityId: number,
  questionId: string,
  answers: object
) => {
  const url = `/opportunities/${opportunityId}/questionnaire/${questionId}`;
  await fetcher.core.update(url, answers);
  mutate(`/${opportunityId}/questionnaire?type=NEED_ANALYSIS`);
};

export function useQuestionnaire(market: string, language: string) {
  const { data } = useSWR<QuestionnaireSettings>(
    "questionnaire.json",
    createQuestionnaireFetcher(market, language)
  );

  return data;
}

export const useB2BQuestionnaireAnswers = (
  customerProjectId?: string,
  partyId?: string
) => {
  return useSWR<AnswersData>(
    customerProjectId && partyId
      ? `/home-project/${customerProjectId}/customer/${partyId}/questionnaire`
      : null,
    (url: string) => fetcher.global.get<AnswersData>(url)
  );
};

export const saveB2BQuestionnaire = async (
  projectId: number,
  answers: object,
  customerProjectId?: string,
  partyId?: string
) => {
  const url = `/${projectId}/questionnaire/b2b`;
  await fetcher.opportunity.update(url, { b2bQuestionnaire: answers });
  if (customerProjectId && partyId) {
    mutate(
      `/home-project/${customerProjectId}/customer/${partyId}/questionnaire`
    );
  }
};
